import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { admin } from '../../api/client.js';

/**
 * Maps paginated reports to the datasets used for their RLS identity.
 * Replaces the jQuery appendGrid screen in the old admin-pagination.hbs.
 */
export default function AdminPaginated() {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState(null);
  const [saved, setSaved] = useState(false);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['admin', 'paginated'], queryFn: admin.getPaginatedDatasets
  });
  const { data: catalogue } = useQuery({
    queryKey: ['admin', 'catalogue'], queryFn: admin.getCatalogue
  });

  const current = draft ?? rows;

  const save = useMutation({
    mutationFn: () => admin.savePaginatedDatasets(
      current.map(r => ({ reportId: r.reportId, datasetIds: r.datasetIds.filter(Boolean) }))
    ),
    onSuccess: () => {
      setDraft(null);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      queryClient.invalidateQueries({ queryKey: ['admin', 'paginated'] });
    }
  });

  const setDataset = (reportId, index, datasetId) => {
    setDraft(current.map(r => {
      if (r.reportId !== reportId) return r;
      const datasetIds = [...r.datasetIds];
      datasetIds[index] = datasetId;
      return { ...r, datasetIds };
    }));
  };

  if (isLoading) return <div className="admin-page"><div className="spinner-border" /></div>;

  return (
    <div className="admin-page">
      <div className="d-flex align-items-center mb-3">
        <h1 className="h4 mb-0">Pagination Setup</h1>
        {saved && <span className="badge bg-success ms-3">Saved</span>}
        <button type="button" className="btn btn-primary btn-sm ms-auto"
                disabled={!draft || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : 'Save changes'}
        </button>
      </div>

      <p className="text-muted small">
        Paginated reports need their datasets declared explicitly so the portal can
        request an embed token with the right row-level security identity.
      </p>

      <table className="table table-sm align-middle">
        <thead><tr><th>Report</th><th>Dataset 1</th><th>Dataset 2 (optional)</th></tr></thead>
        <tbody>
          {current.map(r => (
            <tr key={r.reportId}>
              <td>{r.name}</td>
              {[0, 1].map(i => (
                <td key={i}>
                  <select className="form-select form-select-sm"
                          value={r.datasetIds[i] ?? ''}
                          onChange={e => setDataset(r.reportId, i, e.target.value)}>
                    <option value="">— none —</option>
                    {(catalogue?.datasets ?? []).map(d => (
                      <option key={d.id} value={d.id}>
                        {d.name}{d.requiresRls ? ' (RLS)' : ''}
                      </option>
                    ))}
                  </select>
                </td>
              ))}
            </tr>
          ))}
          {current.length === 0 && (
            <tr><td colSpan={3} className="text-center text-muted py-4">
              No paginated reports found in this workspace.
            </td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
